import { createCraftBriefing } from './191-craft-arcade.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import {
  createWaterworks as createPuzzle,
  rotateWaterworks,
  releaseWaterworks,
  waterworksView,
  waterPorts,
} from '../../../packages/domain/src/64-waterworks.mjs';
const NS = 'http://www.w3.org/2000/svg';
const CHANNEL_ENDPOINTS = [
  [50, 0],
  [100, 50],
  [50, 100],
  [0, 50],
];
/** Accessible channel-building arcade. Water playback follows the deterministic
 * conserved route; transient SVG effects never decide scoring or completion. */
export function createWaterworks(
  ctx,
  { host, seed = 7, allowed = () => true, visible = () => true, onProgress = () => {} } = {},
) {
  const e = (tag, cls, text) => ctx.e(tag, cls, text);
  const frame = e('section', 'waterworks-arcade');
  const hud = e('div', 'waterworks-hud'),
    title = e('strong', '', 'WATERWORKS'),
    score = e('strong', '', '100 DROPS');
  const chain = e('span', 'waterworks-chain');
  hud.append(title, score, chain);
  const goal = e('p', 'waterworks-goal');
  const source = e('div', 'waterworks-source', '💧');
  source.setAttribute('aria-hidden', 'true');
  const surface = e('div', 'waterworks-surface'),
    board = e('div', 'waterworks-board');
  board.setAttribute('role', 'group');
  board.setAttribute('aria-label', 'Rotate channels to carry water from the spring to the bucket');
  const stream = document.createElementNS(NS, 'svg');
  stream.setAttribute('viewBox', '0 0 500 500');
  stream.classList.add('waterworks-stream');
  stream.setAttribute('aria-hidden', 'true');
  const flow = document.createElementNS(NS, 'path');
  flow.setAttribute('fill', 'none');
  flow.setAttribute('stroke-linecap', 'round');
  stream.append(flow);
  surface.append(board, stream);
  const bucket = e('div', 'waterworks-bucket'),
    fill = e('span', 'waterworks-fill'),
    amount = e('strong', '', '0 / 100');
  bucket.append(fill, amount);
  const feedback = e('p', 'waterworks-feedback', 'Turn the channels, then release the spring.');
  feedback.setAttribute('role', 'status');
  const actions = e('div', 'waterworks-actions'),
    release = e('button', '', 'Release water'),
    reset = e('button', '', 'Reset pipes'),
    next = e('button', '', 'Next level');
  for (const b of [release, reset, next]) b.type = 'button';
  paintIconAction(ctx, reset, 'refresh-cw', 'Reset pipes');
  paintIconAction(ctx, next, 'chevron-right', 'Next level', 'Next level');
  actions.append(release, next, reset);
  frame.append(hud, goal, source, surface, bucket, feedback, actions);
  host.append(frame);
  let level = 1,
    state = createPuzzle(seed, level),
    disposed = false,
    clock = 0,
    lastTick = 0,
    animation = 0,
    briefing;
  const turns = new Set();
  const canPlay = () => allowed() && !briefing?.isOpen();
  const buttons = Array.from({ length: 25 }, (_, index) => {
    const b = e('button', 'waterworks-pipe');
    b.type = 'button';
    b.addEventListener('click', () => {
      if (disposed || !canPlay() || animation) return;
      const next = rotateWaterworks(state, index);
      if (next === state) return;
      state = next;
      flow.removeAttribute('d');
      paint();
      if (!globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        const turn = b
          .querySelector('svg')
          ?.animate?.([{ transform: 'rotate(-90deg)' }, { transform: 'rotate(0deg)' }], {
            duration: 180,
            easing: 'cubic-bezier(.2,.8,.2,1)',
          });
        if (turn) {
          turns.add(turn);
          void turn.finished.then(
            () => turns.delete(turn),
            () => turns.delete(turn),
          );
        }
      }
    });
    board.append(b);
    return b;
  });
  const svg = (tag, attrs) => {
    const n = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
    return n;
  };
  function artwork(cell) {
    const node = svg('svg', { viewBox: '0 0 100 100', 'aria-hidden': 'true' });
    const [a, b] = cell.ports.map((p) => CHANNEL_ENDPOINTS[p]);
    const d = `M${a[0]} ${a[1]} Q50 50 ${b[0]} ${b[1]}`;
    for (const [stroke, width] of [
      ['#071c32', 36],
      ['#639ab5', 29],
      ['#c5eff8', 22],
      ['#123b5c', 15],
    ])
      node.append(
        svg('path', { d, fill: 'none', stroke, 'stroke-width': width, 'stroke-linecap': 'butt' }),
      );
    node.append(
      svg('circle', {
        cx: 50,
        cy: 50,
        r: 6,
        fill: '#67e7ee',
        stroke: '#efffff',
        'stroke-width': 2,
      }),
    );
    return node;
  }
  function paint() {
    const view = waterworksView(state);
    const preview = releaseWaterworks(state).result;
    chain.textContent = preview.complete ? 'READY TO FLOW ✦' : `${preview.route.length} LINKED`;
    chain.dataset.ready = String(preview.complete);
    chain.setAttribute(
      'aria-label',
      preview.complete
        ? 'Current channels connect the spring to the bucket'
        : `${preview.route.length} channels connected from the spring`,
    );
    title.textContent = `WATERWORKS · ${level}`;
    // Keep the earned result pending until its visible water has arrived.
    score.textContent =
      state.complete && !animation ? `${state.score.toLocaleString()} PTS` : `${state.moves} TURNS`;
    goal.textContent = view.goal;
    frame.dataset.complete = String(state.complete && !animation);
    frame.dataset.steam = String(Boolean(state.result?.steam) && !animation);
    next.hidden = !state.complete || Boolean(animation);
    release.hidden = !next.hidden;
    next.disabled = disposed || !canPlay() || Boolean(animation);
    release.disabled = disposed || !canPlay() || Boolean(animation) || state.complete;
    reset.disabled = disposed || !canPlay() || Boolean(animation);
    view.cells.forEach((cell, i) => {
      const b = buttons[i];
      b.disabled = !cell.active || disposed || !canPlay() || Boolean(animation) || state.complete;
      b.dataset.active = String(cell.active);
      b.dataset.connected = String(preview.route.includes(i));
      b.dataset.leak = String(!preview.complete && i === (preview.route.at(-1) ?? state.source));
      b.dataset.fire = String(cell.fire);
      b.dataset.gate = String(cell.gate);
      b.dataset.wet = String(Boolean(state.result?.route.includes(i)) && !animation);
      b.setAttribute(
        'aria-label',
        `${cell.fire ? 'Fire chamber, ' : cell.gate ? 'Steam gate, ' : ''}channel row ${Math.floor(i / 5) + 1}, column ${(i % 5) + 1}: ${cell.ports.map((d) => ['up', 'right', 'down', 'left'][d]).join(' to ')}. Rotate clockwise.`,
      );
      b.replaceChildren();
      if (cell.active) b.append(artwork(cell));
      if (cell.fire || cell.gate) {
        const marker = e('span', 'waterworks-marker', cell.fire ? '🔥' : '⚙');
        marker.setAttribute('aria-hidden', 'true');
        b.append(marker);
      }
    });
    if (!animation) {
      const water = state.result?.delivered ?? 0;
      amount.textContent = `${water} / ${state.fire >= 0 ? 88 : 100}`;
      fill.style.height = `${water}%`;
    }
  }
  function finishAnimation() {
    animation = 0;
    onProgress({ score: state.score, stage: level, completed: state.complete });
    flow.style.strokeDashoffset = '0';
    feedback.textContent = state.complete
      ? `${state.result.delivered} drops saved${state.result.steam ? ' · 12 drops powered the steam gate' : ''}! +${state.score.toLocaleString()} points.`
      : `Leak after ${state.result.route.length} channels. Follow the amber outlet, rotate its next join and try again.`;
    paint();
  }
  release.addEventListener('click', () => {
    if (disposed || !canPlay() || animation || state.complete) return;
    state = releaseWaterworks(state);
    // Follow the very same endpoint–centre–endpoint quadratic as the pipe
    // artwork. Reciprocal ports join on cell boundaries, never through corners.
    let inlet = 0;
    let d = `M${(state.source % 5) * 100 + 50} 0`;
    for (const index of state.result.route) {
      const x = (index % 5) * 100,
        y = Math.floor(index / 5) * 100;
      const exit = waterPorts(state.cells[index]).find((port) => port !== inlet);
      const endpoint = CHANNEL_ENDPOINTS[exit];
      d += ` Q${x + 50} ${y + 50} ${x + endpoint[0]} ${y + endpoint[1]}`;
      inlet = (exit + 2) % 4;
    }
    flow.setAttribute('d', d);
    const length = flow.getTotalLength();
    flow.style.strokeDasharray = String(length);
    flow.style.strokeDashoffset = String(length);
    animation = Math.max(1, clock);
    feedback.textContent = 'Spring released… follow the flow';
    paint();
    onProgress({ score: 0, stage: level, completed: false });
    if (
      !visible() ||
      document.hidden ||
      globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    )
      finishAnimation();
    else controller.refresh();
  });
  reset.addEventListener('click', () => {
    if (!canPlay() || animation) return;
    state = createPuzzle(seed, level);
    onProgress({ score: state.score, stage: level, completed: state.complete });
    flow.removeAttribute('d');
    feedback.textContent = 'Fresh pipes. Turn each junction to guide the water.';
    paint();
  });
  next.addEventListener('click', () => {
    if (!canPlay() || animation || !state.complete) return;
    level = Math.min(100, level + 1);
    state = createPuzzle(seed, level);
    onProgress({ score: state.score, stage: level, completed: state.complete });
    flow.removeAttribute('d');
    feedback.textContent =
      level > 1
        ? 'Water quenches the fire. Its steam opens the gate below.'
        : 'Turn the channels, then release the spring.';
    paint();
  });
  paint();
  function cancel() {
    for (const turn of turns) turn.cancel();
    turns.clear();
    lastTick = 0;
    if (animation) finishAnimation();
  }
  let raf = 0;
  function loop(now) {
    raf = 0;
    if (disposed || !canPlay() || !visible() || document.hidden) {
      lastTick = 0;
      return;
    }
    controller.tick(now);
    if (animation) raf = requestAnimationFrame(loop);
    else lastTick = 0;
  }
  const controller = {
    tick(now) {
      if (disposed || !canPlay() || document.hidden) {
        lastTick = 0;
        return;
      }
      const delta = lastTick ? Math.min(80, Math.max(0, now - lastTick)) : 0;
      lastTick = now;
      clock += delta;
      if (animation) {
        const t = Math.min(1, (clock - animation) / 1800),
          length = flow.getTotalLength();
        flow.style.strokeDashoffset = String(length * (1 - t));
        if (state.complete) {
          const delivered = Math.round(state.result.delivered * Math.max(0, (t - 0.75) / 0.25));
          fill.style.height = `${delivered}%`;
          amount.textContent = `${delivered} / ${state.fire >= 0 ? 88 : 100}`;
        }
        const reached = Math.floor(t * state.result.route.length);
        frame.dataset.steam = String(
          state.fire >= 0 &&
            state.result.route.indexOf(state.fire) >= 0 &&
            reached > state.result.route.indexOf(state.fire),
        );
        if (t === 1) finishAnimation();
      }
    },
    refresh() {
      frame.dataset.paused = String(!visible() || !canPlay() || document.hidden);
      if (!canPlay()) cancel();
      if (disposed || !canPlay() || !visible() || document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
        lastTick = 0;
      } else if (animation && !raf) raf = requestAnimationFrame(loop);
      return !disposed && canPlay();
    },
    cancel,
    pause() {
      cancelAnimationFrame(raf);
      raf = 0;
      cancel();
    },
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      animation = 0;
      for (const turn of turns) turn.cancel();
      turns.clear();
      briefing.dispose();
      frame.remove();
    },
  };
  briefing = createCraftBriefing(ctx, {
    host: frame,
    title: 'Wake the waterworks',
    steps: [
      ['↻', 'Tap a channel to rotate it. Join its openings to the next pipe.'],
      ['💧 → ▱', 'Follow the glowing connected chain from spring to bucket.'],
      ['🔥 → ⚙', 'Later, pass through fire to make steam and open the gate.'],
    ],
    onOpen: () => controller.pause(),
    onClose: () => {
      paint();
      controller.refresh();
      buttons[state.source]?.focus?.({ preventScroll: true });
    },
  });
  const help = e('button', 'craft-help', 'How to play');
  help.type = 'button';
  paintIconAction(ctx, help, 'circle-help', 'How to play');
  help.addEventListener('click', () => briefing.open(help));
  actions.append(help);
  paint();
  controller.refresh();
  return controller;
}
